import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { historyAPI, getImageUrl } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

export default function HistoryForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const isEditMode = Boolean(id);

  const [loading, setLoading] = useState(isEditMode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    year: '',
    course_name: '',
    champion_name: '',
    bowl_winner_name: '',
    image_url: '',
    image_url_2: '',
    google_photos_url: '',
    content: ''
  });

  // Image upload state
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/history');
      return;
    }

    if (isEditMode) {
      fetchHistoryEntry();
    }
  }, [id, isAuthenticated]);

  const fetchHistoryEntry = async () => {
    try {
      setLoading(true);
      const data = await historyAPI.getById(parseInt(id));
      setFormData({
        year: data.year.toString(),
        course_name: data.course_name || '',
        champion_name: data.champion_name || '',
        bowl_winner_name: data.bowl_winner_name || '',
        image_url: data.image_url || '',
        image_url_2: data.image_url_2 || '',
        google_photos_url: data.google_photos_url || '',
        content: data.content || ''
      });
    } catch (err) {
      console.error('Error loading history entry:', err);
      setError('Failed to load history entry');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);

      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.year || !formData.course_name || !formData.champion_name || !formData.content) {
      setError('Please fill in all required fields (Year, Course Name, Champion Name, and Content)');
      return;
    }

    const yearInt = parseInt(formData.year);
    if (isNaN(yearInt) || yearInt < 1900 || yearInt > 2100) {
      setError('Please enter a valid year between 1900 and 2100');
      return;
    }

    try {
      setSaving(true);

      const payload = {
        year: yearInt,
        course_name: formData.course_name.trim(),
        champion_name: formData.champion_name.trim(),
        bowl_winner_name: formData.bowl_winner_name.trim() || null,
        image_url: formData.image_url.trim() || null,
        image_url_2: formData.image_url_2.trim() || null,
        google_photos_url: formData.google_photos_url.trim() || null,
        content: formData.content.trim()
      };

      let entryId = parseInt(id);

      if (isEditMode) {
        await historyAPI.update(entryId, payload);
      } else {
        const newEntry = await historyAPI.create(payload);
        entryId = newEntry.id;
      }

      // Upload image if one was selected
      if (selectedImage) {
        try {
          await historyAPI.uploadImage(entryId, selectedImage);
        } catch (uploadErr) {
          console.error('Error uploading image:', uploadErr);
          // Entry was saved, but image upload failed
          // Show warning but don't prevent navigation
          alert('Entry saved, but image upload failed. Please try uploading the image again by editing the entry.');
        }
      }

      navigate('/history');
    } catch (err) {
      console.error('Error saving history entry:', err);
      let errorMessage = 'Failed to save history entry';

      if (err.response?.status === 401) {
        errorMessage = 'You must be logged in as admin to edit history entries. Please log in and try again.';
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      }

      setError(errorMessage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/history');
  };

  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="pb-8">
      {error && (
        <div className="bg-red-600 text-white px-4 py-3 mb-4 mx-4 md:mx-8 lg:mx-12 max-w-4xl mx-auto rounded-lg">
          {error}
        </div>
      )}

      <div className="px-4 md:px-8 lg:px-12 max-w-4xl mx-auto">
        <form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <Card title="Basic Information" className="mb-6">
            <div className="space-y-4">
              {/* Year */}
              <div>
                <label className="block text-base font-semibold text-gray-700 mb-2">
                  Year *
                </label>
                <input
                  type="number"
                  value={formData.year}
                  onChange={(e) => handleChange('year', e.target.value)}
                  placeholder="e.g., 2024"
                  min="1900"
                  max="2100"
                  className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>

              {/* Course Name */}
              <div>
                <label className="block text-base font-semibold text-gray-700 mb-2">
                  Course Name *
                </label>
                <input
                  type="text"
                  value={formData.course_name}
                  onChange={(e) => handleChange('course_name', e.target.value)}
                  placeholder="e.g., Royal Troon Golf Club"
                  className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>

              {/* Champion Name */}
              <div>
                <label className="block text-base font-semibold text-gray-700 mb-2">
                  Champion Name *
                </label>
                <input
                  type="text"
                  value={formData.champion_name}
                  onChange={(e) => handleChange('champion_name', e.target.value)}
                  placeholder="e.g., John Smith"
                  className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  required
                />
              </div>

              {/* Bowl Winner Name */}
              <div>
                <label className="block text-base font-semibold text-gray-700 mb-2">
                  Bowl Winner (Optional)
                </label>
                <input
                  type="text"
                  value={formData.bowl_winner_name}
                  onChange={(e) => handleChange('bowl_winner_name', e.target.value)}
                  placeholder="e.g., John Smith"
                  className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="text-sm text-gray-500 mt-1">
                  The Bowl is awarded to players outside top 6 after first round who win the second round in their group
                </p>
              </div>
            </div>
          </Card>

          {/* Media & Links */}
          <Card title="Media & Links (Optional)" className="mb-6">
            <div className="space-y-4">
              {/* Hero Image */}
              <div>
                <label className="block text-base font-semibold text-gray-700 mb-2">
                  Hero Image
                </label>

                {/* Current or Preview Image */}
                {(imagePreview || formData.image_url) && (
                  <div className="mb-3">
                    <img
                      src={imagePreview || getImageUrl(formData.image_url)}
                      alt="Preview"
                      className="w-full max-w-md h-48 object-cover rounded-lg border-2 border-gray-300"
                    />
                  </div>
                )}

                {/* Image URL Input */}
                <div>
                  <input
                    type="text"
                    value={formData.image_url}
                    onChange={(e) => handleChange('image_url', e.target.value)}
                    placeholder="/images/history/2024-troon.jpg or https://example.com/image.jpg"
                    className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <p className="text-sm text-gray-600 mt-2">
                    Add photos to <code className="bg-gray-100 px-1 py-0.5 rounded">Assets/History/</code> folder, then enter: <code className="bg-gray-100 px-1 py-0.5 rounded">/images/history/filename.jpg</code>
                    <br />
                    Or use an external URL (e.g., Google Photos link)
                  </p>
                </div>
              </div>

              {/* Second Image (Bottom of Narrative) */}
              <div>
                <label className="block text-base font-semibold text-gray-700 mb-2">
                  Second Image (Bottom of Narrative)
                </label>

                {/* Preview if exists */}
                {formData.image_url_2 && (
                  <div className="mb-3">
                    <img
                      src={getImageUrl(formData.image_url_2)}
                      alt="Second Image Preview"
                      className="w-full max-w-md h-48 object-cover rounded-lg border-2 border-gray-300"
                    />
                  </div>
                )}

                {/* Image URL Input */}
                <div>
                  <input
                    type="text"
                    value={formData.image_url_2}
                    onChange={(e) => handleChange('image_url_2', e.target.value)}
                    placeholder="/images/history/2024-troon-2.jpg"
                    className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <p className="text-sm text-gray-600 mt-2">
                    Optional second image displayed at the bottom of the narrative
                  </p>
                </div>
              </div>

              {/* Google Photos URL */}
              <div>
                <label className="block text-base font-semibold text-gray-700 mb-2">
                  Google Photos Album URL
                </label>
                <input
                  type="url"
                  value={formData.google_photos_url}
                  onChange={(e) => handleChange('google_photos_url', e.target.value)}
                  placeholder="https://photos.google.com/share/..."
                  className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <p className="text-sm text-gray-600 mt-1">
                  Link to a Google Photos album with event photos
                </p>
              </div>
            </div>
          </Card>

          {/* Content */}
          <Card title="Event Story *" className="mb-6">
            <div>
              <textarea
                value={formData.content}
                onChange={(e) => handleChange('content', e.target.value)}
                placeholder="Write the narrative history of this year's event. Use double line breaks to separate paragraphs."
                rows={12}
                className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary font-sans"
                required
              />
              <p className="text-sm text-gray-600 mt-2">
                Tip: Use two line breaks (press Enter twice) to create new paragraphs
              </p>
            </div>
          </Card>

          {/* Actions */}
          <Card>
            <div className="flex gap-4">
              <Button
                type="submit"
                variant="primary"
                disabled={saving}
                className="flex-1"
              >
                {saving ? 'Saving...' : isEditMode ? 'Update Entry' : 'Create Entry'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={handleCancel}
                disabled={saving}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </Card>
        </form>
      </div>
    </div>
  );
}
